import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from './api';

/**
 * Distribuição por disponibilidade: o "Disponível" de cada um, o painel da
 * equipe (gestor), o clique no WhatsApp (1º contato) e o relatório.
 */
export interface MyAvailability {
  available: boolean;
  changedAt: string | null;
  absentUntil: string | null;
  absentReason: string | null;
  pending: number;
}

export interface TeamMember extends MyAvailability {
  userId: string;
  name: string;
  email: string;
  absentByName: string | null;
  today: number;
}

export interface QueuedLead {
  id: string;
  businessId: string;
  code: number;
  leadName: string;
  pipelineName: string;
  queuedAt: string;
}

export interface ReportRow {
  attendantId: string | null;
  name: string;
  received: number;
  contacted: number;
  withinSla: number;
  avgMinutes: number | null;
  expired: number;
  availableHours: number;
}

export interface DistributionReport {
  pipelineId: string;
  from: string;
  to: string;
  slaMinutes: number;
  rows: ReportRow[];
  totals: Omit<ReportRow, 'attendantId' | 'name'> & {
    avgQueueMinutes: number | null;
    hasWhatsappClicks: boolean;
  };
}

const data = <T>(promise: Promise<{ data: T }>) => promise.then((response) => response.data);

export const distributionApi = {
  me: () => data<MyAvailability>(api.get('/distribution/me')),
  setMe: (available: boolean) =>
    data<MyAvailability>(api.put('/distribution/me', { available })),
  team: () =>
    data<{ members: TeamMember[]; queue: QueuedLead[] }>(api.get('/distribution/team')),
  setMember: (userId: string, available: boolean) =>
    data(api.put(`/distribution/team/${userId}`, { available })),
  setAbsence: (userId: string, until: string | null, reason?: string) =>
    data(api.put(`/distribution/team/${userId}/absence`, { until, reason })),
  report: (params: { pipelineId: string; from: string; to: string; sla?: number }) =>
    data<DistributionReport>(api.get('/distribution/report', { params })),
};

/**
 * Clique no WhatsApp do lead: conta como 1º contato. Não espera nem avisa
 * erro — o WhatsApp abre de qualquer jeito.
 */
export function recordWhatsappClick(leadId: string | null | undefined) {
  if (!leadId) return;
  void api.post('/distribution/contact', { leadId }).catch(() => undefined);
}

/** Meu "Disponível" — relido a cada 30 s (o prazo vencido pode desligar). */
export function useMyAvailability() {
  const queryClient = useQueryClient();
  const query = useQuery({
    queryKey: ['distribution', 'me'],
    queryFn: distributionApi.me,
    refetchInterval: 30_000,
  });
  const toggle = useMutation({
    mutationFn: (available: boolean) => distributionApi.setMe(available),
    onSuccess: (me) => {
      queryClient.setQueryData(['distribution', 'me'], me);
      queryClient.invalidateQueries({ queryKey: ['distribution', 'team'] });
    },
  });
  return { me: query.data, toggle };
}
